import React from 'react';
import { GetServerSideProps } from 'next';
import { getRequestContext } from 'server/utils';
import { LandingPageConfig } from 'src/generated/landing-page-config-schemas/v1/type';
import SliceRenderer from '../../components/LandingPage/SliceRenderer';
import LayoutV2 from '../../components/LayoutV2';
import Header from '../../components/LandingPage/v1/Header';
import Footer from 'src/components/LandingPage/v1/Footer';
import { fetchLandingPageDraft } from 'src/services/panacea/landing-page';
import logger from 'logger';
type Props = {
  config: LandingPageConfig;
};

const STATIC_BACKGROUND_SLICES = ['hero', 'call-to-action-banner'];

/**
 * Components alternate between white and gray backgrounds,
 starting with gray since the hero section is white.
 Because slices can be conditionally shown/hidden or reordered,
 this function determines the correct background color
 based on the slice’s index among the visible alternating sections.
  */
const determineBackgroundColor = (index: number): string => {
  return index % 2 === 0 ? '#F7F7F7' : '#FFFFFF';
};

const LandingPage: React.FC<Props> = ({ config }) => {
  let alternatingIndex = 0;

  const slicesWithBackgrounds = config.data.slices.map((slice) => {
    if (STATIC_BACKGROUND_SLICES.includes(slice.type)) {
      // Hero and CTA use their own backgrounds
      return { slice, backgroundColor: undefined };
    }
    const backgroundColor = determineBackgroundColor(alternatingIndex);
    alternatingIndex++;
    return { slice, backgroundColor };
  });

  return (
    <LayoutV2
      header={<Header {...config.data.header} />}
      footer={<Footer {...config.data.footer} />}
      fullWidth
    >
      <div style={{ marginBottom: '-2rem' }}>
        {slicesWithBackgrounds.map(({ slice, backgroundColor }, index) => (
          <SliceRenderer
            key={`${slice.type}-${index}`}
            slice={slice}
            backgroundColor={backgroundColor}
          />
        ))}
      </div>
    </LayoutV2>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const { tenant } = await getRequestContext(ctx.req);

  let config = tenant.activeLandingPageConfig as LandingPageConfig;

  // Preview a specific draft when its uuid is supplied via `?draft=<uuid>`.
  // If the draft can't be fetched (e.g. unknown uuid), render the 404 page.
  const draftUuid = ctx.query.draft;
  if (typeof draftUuid === 'string') {
    try {
      config = (await fetchLandingPageDraft(draftUuid)) as LandingPageConfig;
    } catch (err) {
      logger.warn({
        msg: 'FailedToFetchDraftLandingPageReturning404',
        noticable: err as Error,
      });
      return { notFound: true };
    }
  }

  return {
    props: {
      config,
    },
  };
};

export default LandingPage;
